import videojs from 'video.js';
import { makeVTT } from './captions/make-vtt.js';
import './captions.css';
import { localStorageAvailable } from '../../util';

const SETTINGS_KEY = 'mediasite-player-captions-track';
const _initialTrack = new WeakMap();
const _enabled = new WeakMap();
let localize;

/**
 * The Captions module.
 * @member {CaptionsPlugin} captions
 * @memberof MediasitePlayer#
 */

/**
 * Configures closed captions that may be displayed during playback.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class CaptionsPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        _enabled.set(this, true);
        if (localStorageAvailable()) {
            this.initialTrackLanguage = localStorage[SETTINGS_KEY];
        }

        player.ready(() => {
            this.#updateControlBarButtonVisibility();
            this.on(player, ['useractive', 'userinactive'], this.#handleUserActiveChange);
            this.on(player, 'texttrackchange', this.#handleTextTrackChange);

            player.collapsedButtons?.push({
                pluginName: this.name,
                label: localize('Captions'),
                buttonName: 'SubsCapsButton',
                class: 'vjs-subs-caps-button'
            });
        });
    }

    /**
    * Sets a list of captions that should be available to the user in the player UI.
    * @param {string} language The language code for the provided captions.
    * @param {string} label A label to use in the UI for this set of captions.
    * @param {string} captionsUrl A url to a vtt file stream
    * @example
    * player.captions.set('en', 'English', 'https:/dev-machine.sonicfoundry.net/Mediasite/streams/FileServer/Presentation/PresentationId/captions_languageCode.vtt?playbackTicket=ticketId');
    */
    set(language, label, captionsUrl) {
        const tracks = this.player.textTracks();
        const initialTrackLanguage = this.initialTrackLanguage;

        for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].language === language && tracks[i].kind === 'captions') {
                const oldTrack = tracks[i];
                this.off(oldTrack, ['cuechange', 'modechange'], this.#handleCaptionChange);
                this.player.removeRemoteTextTrack(oldTrack);
                break;
            }
        }

        if (captionsUrl) {
            const track = this.player.addRemoteTextTrack({
                src: captionsUrl,
                kind: 'captions',
                label: label ?? language,
                language: language,
                mode: 'disabled'
            });

            this.on(track, ['cuechange', 'modechange'], this.#handleCaptionChange);
        }
    }

    /**
    * Gets the list of captions that are available for the given language.
    * If no language is passed, returns the list of active captions
    * @param {string} language The language code of the captions to be retrieved.
    * @returns {VTTCue[]}
    * @example
    * const englishCaptions = player.captions.get('en');
    */
    get(language) {
        const tracks = this.player.textTracks();
        const captions = [];

        // Check for active caption language if no language passed to get
        if (language == null) {
            language = Array.from(tracks).find(track => track.mode === 'showing')?.language;
        }

        for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].language === language && tracks[i].kind === 'captions') {
                captions.push(...Array.from(tracks[i].cues ?? []));
                break;
            }
        }
        return captions;
    }

    /**
     * Returns an array containing VTTCue objects for the captions that apply to the current playback time, if any.
     * @param {string} language
     * @returns {VTTCue[]}
     * @example
     * const currentlyDisplayedCaptions = player.captions.getCurrentCaptions('en');
     */
    getCurrentCaptions(language) {
        const captions = [];

        if (language !== null) {
            const tracks = Array.from(this.player.textTracks()).filter(track => track.kind === 'captions');
            const languageToCheck = language || tracks[0]?.language; // Fall back to the first language if nothing is passed

            for (const track of Array.from(tracks)) {
                if (track.language === languageToCheck) {
                    if (track.activeCues) {
                        captions.push(...Array.from(track.activeCues));
                    }                    
                }
            }
        }
        
        return captions;
    }

    /**
     * Returns the TextTrack that corresponds to a given language
     */
    getCaptionTrack(language) {
        if (language == null) {
            return;
        }
        const tracks = this.player.textTracks();
        let captionTrack;
        for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].kind === 'captions' && tracks[i].language === language) {
                captionTrack = tracks[i];
            }
        }

        return captionTrack;
    }

    /**
     * Returns the TextTracks that correspond to the captions
     */
    getAllCaptionTracks() {
        const tracks = this.player.textTracks();
        const captionTracks = [];
        for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].kind === 'captions') {
                captionTracks.push(tracks[i]);
            }
        }

        return captionTracks;
    }

    /**
     * Used to toggle the captions either on or off. Currently used in the hot keys 
    */
    toggleCaptions() {
        this.visible = !this.visible;
    }

    /**
     * The language of the captions track that should be showing. When this value is a string (for example, <code>'en'</code>), the track with the matching language will be shown. If this value is <code>true</code>, the first available track (if any) will be shown.
     * @type {?(string|boolean)}
     * @example <caption>Showing English captions (if available).</caption>
     * player.captions.visible = 'en';
     */
    set visible(language) {
        const tracks = Array.from(this.player.textTracks()).filter(track => track.kind === 'captions');

        let found = false;
        for (const track of tracks) {
            if (track.language === language || (language === true && !found)) {
                track.mode = 'showing';
                found = true;
            }
            else {
                track.mode = 'hidden';
            }
        }
    }

    get visible() {
        const tracks = Array.from(this.player.textTracks());
        return tracks.find(track => {
            return track.mode === 'showing';
        })?.language ?? null;
    }

    /**
     * Whether or not to enable the captions UI.
     * @type {boolean}
     * @example
     * player.captions.enabled = true;
     */
    get enabled() {
        return _enabled.get(this);
    }

    set enabled(enabled) {
        _enabled.set(this, enabled);
        this.#updateControlBarButtonVisibility();
    }


    /**
     * What caption text track language should load if any.
     * @type {string}
     * @example
     * player.captions.initialTrackLanguage = "en-US";
     */
    get initialTrackLanguage() {
        return _initialTrack.get(this);
    }

    set initialTrackLanguage(track) {
        _initialTrack.set(this, track);
    }

    #updateControlBarButtonVisibility() {
        const subsCapsButton = this.player.getDescendant('ControlBar', 'SubsCapsButton');
        const hasCaptionTracks = Array.from(this.player.textTracks()).filter(track => track.kind === 'captions').length > 0;
        const hasLiveCaptions = this.player.usingPlugin('live') && this.player.live().liveCaptions;

        if (_enabled.get(this) && (hasCaptionTracks || hasLiveCaptions)) {
            subsCapsButton.removeClass('vjs-hidden');
        }
        else {
            subsCapsButton.addClass('vjs-hidden');
        }
    }

    #handleCaptionChange() {
        const currentCaptions = this.getCurrentCaptions(this.visible);
        this.player.trigger('captionchanged', currentCaptions);
    }

    #handleUserActiveChange() {
        this.player.getChild('TextTrackDisplay').updateDisplay();
    }

    #handleTextTrackChange() {
        const captionTracks = Array.from(this.player.textTracks()).filter(track => track.kind === 'captions');
        if (captionTracks.length > 0) {
            const currentTrack = captionTracks.find(track => track.mode === 'showing');
            //if the caption track exists and has changed, the captions have changed
            this.#handleCaptionChange();
            if (localStorageAvailable()) {
                localStorage[SETTINGS_KEY] = currentTrack?.language;
            }
        }
    }

    clear() {
        const captionTracks = this.getAllCaptionTracks();

        captionTracks.forEach((track) => {
            this.player.removeRemoteTextTrack(track);
        })
        
        this.enabled = false;
    }

    dispose() {
        this.off(this.player, ['useractive', 'userinactive'], this.#handleUserActiveChange);
        this.off(this.player, 'texttrackchange', this.#handleTextTrackChange);
        super.dispose();
    }
}

export default CaptionsPlugin;
